/**
 * Sentry Error Monitoring Configuration
 *
 * This module configures Sentry for error tracking and observability.
 * Sentry is disabled by default in dev mode but can be enabled via
 * the VITE_SENTRY_ENABLED environment variable for testing.
 *
 */

import * as Sentry from '@sentry/react';
import { ENV, isDev } from './env';

/**
 * Initialize Sentry error monitoring
 */
export const initializeSentry = (): void => {
  const dsn = import.meta.env.VITE_SENTRY_DSN || '';

  // Sentry is disabled in dev mode by default, but can be enabled for testing
  // Set VITE_SENTRY_ENABLED=true in .env.local to test Sentry in dev mode
  const enabledOverride = import.meta.env.VITE_SENTRY_ENABLED;
  const enabled = enabledOverride !== undefined
    ? enabledOverride === 'true'
    : !isDev();

  if (!enabled) {
    if (isDev()) {
      console.log('[Sentry] Disabled in dev mode. Set VITE_SENTRY_ENABLED=true to enable.');
    }
    return;
  }

  if (!dsn) {
    console.warn('[Sentry] DSN not configured. Set VITE_SENTRY_DSN environment variable.');
    return;
  }

  // App version for release tracking (set via CI/CD or package.json)
  const release = import.meta.env.VITE_APP_VERSION
    ? `influenster-creators-web@${import.meta.env.VITE_APP_VERSION}`
    : undefined;

  Sentry.init({
    dsn,
    environment: ENV.env,
    enabled,

    // Release tracking - enables source maps correlation in Sentry dashboard
    // Set VITE_APP_VERSION in your build pipeline for proper release tracking
    release,

    // Performance monitoring - keep low to avoid exceeding quota
    // Mirrors mobile app setting of 0.01 (1%)
    tracesSampleRate: 0.01,

    // Browser tracing for performance monitoring (page loads, route changes)
    integrations: [Sentry.browserTracingIntegration()],

    // Filter out common non-actionable browser errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
  });

  console.log(`[Sentry] Initialized for ${ENV.env} environment${release ? ` (${release})` : ''}`);
};

/**
 * Set user context for Sentry
 * Call this after user authentication
 */
export const setSentryUser = (user: { id: string; email?: string } | null): void => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
    });
  } else {
    Sentry.setUser(null);
  }
};

/**
 * Capture a custom error with additional context
 */
export const captureError = (error: Error, context?: Record<string, unknown>): void => {
  Sentry.captureException(error, {
    extra: context,
  });
};

/**
 * Capture a custom message
 */
export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info'): void => {
  Sentry.captureMessage(message, level);
};

// Re-export Sentry's ErrorBoundary for use in App
export const SentryErrorBoundary = Sentry.ErrorBoundary;
